import React, { useState, useEffect } from 'react';
import { auth, db } from './firebaseConfig';
import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  orderBy,
  where,
  Timestamp
} from 'firebase/firestore';

const ALLOWED_EMAILS = ['ryrywatson@gmail.com', 'ryan@carebeacon.us'];

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && ALLOWED_EMAILS.includes(user.email)) {
        setUser(user);
        loadData();
      } else if (user) {
        // Logged in but not authorized
        signOut(auth);
        setError('Access denied. You are not authorized to access this portal.');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadData = async () => {
    try {
      // Load clients
      const clientsSnap = await getDocs(collection(db, 'clients'));
      const clientsData = [];
      for (const docSnap of clientsSnap.docs) {
        const client = { id: docSnap.id, ...docSnap.data() };
        // Load billing config for each client
        const configSnap = await getDoc(doc(db, 'billingConfig', docSnap.id));
        if (configSnap.exists()) {
          client.billingConfig = configSnap.data();
        }
        clientsData.push(client);
      }
      setClients(clientsData);

      // Load invoices
      const invoicesSnap = await getDocs(
        query(collection(db, 'invoices'), orderBy('createdAt', 'desc'))
      );
      setInvoices(invoicesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const handleLogin = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      setError('Login failed: ' + err.message);
    }
  };

  const handleLogout = () => {
    signOut(auth);
    setUser(null);
  };

  const updateBillingConfig = async (clientId, config) => {
    try {
      await updateDoc(doc(db, 'billingConfig', clientId), {
        ...config,
        updatedAt: Timestamp.now()
      });
      await loadData();
      setShowModal(false);
    } catch (err) {
      console.error('Error updating config:', err);
      alert('Error updating billing config');
    }
  };

  if (loading) {
    return (
      <div className="login-container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>CareBeacon Billing</h1>
          <p>Platform Administration Portal</p>
          <button className="login-btn" onClick={handleLogin}>
            Sign in with Google
          </button>
          {error && <div className="error-msg">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        user={user}
        onLogout={handleLogout}
      />
      <main className="main-content">
        {currentPage === 'dashboard' && (
          <Dashboard clients={clients} invoices={invoices} />
        )}
        {currentPage === 'clients' && (
          <ClientsPage
            clients={clients}
            onEdit={(client) => { setSelectedClient(client); setShowModal(true); }}
          />
        )}
        {currentPage === 'invoices' && (
          <InvoicesPage invoices={invoices} clients={clients} />
        )}
        {currentPage === 'usage' && (
          <UsagePage clients={clients} />
        )}
      </main>

      {showModal && selectedClient && (
        <BillingConfigModal
          client={selectedClient}
          onClose={() => { setShowModal(false); setSelectedClient(null); }}
          onSave={(config) => updateBillingConfig(selectedClient.id, config)}
        />
      )}
    </div>
  );
}

function Sidebar({ currentPage, setCurrentPage, user, onLogout }) {
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'clients', label: 'Clients', icon: '🏢' },
    { id: 'invoices', label: 'Invoices', icon: '📄' },
    { id: 'usage', label: 'Usage Reports', icon: '📈' },
  ];

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <h2>CareBeacon</h2>
        <span>Billing Portal</span>
      </div>
      <nav>
        {navItems.map(item => (
          <div
            key={item.id}
            className={`nav-item ${currentPage === item.id ? 'active' : ''}`}
            onClick={() => setCurrentPage(item.id)}
          >
            <span>{item.icon}</span>
            {item.label}
          </div>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="user-info">{user.email}</div>
        <button className="logout-btn" onClick={onLogout}>Sign Out</button>
      </div>
    </aside>
  );
}

function Dashboard({ clients, invoices }) {
  const activeClients = clients.filter(c => c.status === 'active').length;
  const totalRevenue = invoices
    .filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + (i.total || 0), 0);
  const pendingInvoices = invoices.filter(i => i.status === 'sent').length;
  const overdueInvoices = invoices.filter(i => i.status === 'overdue').length;

  return (
    <>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Overview of your billing platform</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card primary">
          <h4>Active Clients</h4>
          <div className="value">{activeClients}</div>
          <div className="subtext">{clients.length} total registered</div>
        </div>
        <div className="stat-card success">
          <h4>Total Revenue</h4>
          <div className="value">${totalRevenue.toFixed(2)}</div>
          <div className="subtext">All time paid invoices</div>
        </div>
        <div className="stat-card warning">
          <h4>Pending Invoices</h4>
          <div className="value">{pendingInvoices}</div>
          <div className="subtext">Awaiting payment</div>
        </div>
        <div className="stat-card danger">
          <h4>Overdue</h4>
          <div className="value">{overdueInvoices}</div>
          <div className="subtext">Past due date</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Recent Invoices</h3>
        </div>
        {invoices.length === 0 ? (
          <div className="empty-state">
            <h3>No invoices yet</h3>
            <p>Invoices will be generated on the 1st of each month</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {invoices.slice(0, 5).map(invoice => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoiceNumber || invoice.id}</td>
                    <td>{invoice.clientName}</td>
                    <td>${(invoice.total || 0).toFixed(2)}</td>
                    <td><span className={`badge ${invoice.status}`}>{invoice.status}</span></td>
                    <td>{invoice.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Clients</h3>
        </div>
        {clients.length === 0 ? (
          <div className="empty-state">
            <h3>No clients registered</h3>
            <p>Add clients through the Firebase Console</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Project ID</th>
                  <th>Pricing Model</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(client => (
                  <tr key={client.id}>
                    <td><strong>{client.name}</strong></td>
                    <td>{client.projectId}</td>
                    <td>{client.billingConfig?.pricingModel || 'Not configured'}</td>
                    <td><span className={`badge ${client.status}`}>{client.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function ClientsPage({ clients, onEdit }) {
  return (
    <>
      <div className="page-header">
        <h1>Clients</h1>
        <p>Manage client billing configurations</p>
      </div>

      <div className="card">
        {clients.length === 0 ? (
          <div className="empty-state">
            <h3>No clients registered</h3>
            <p>Add clients through the Firebase Console</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Company Code</th>
                  <th>Pricing</th>
                  <th>Staff Rate</th>
                  <th>Patient Rate</th>
                  <th>Billing Contact</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(client => (
                  <tr key={client.id}>
                    <td><strong>{client.name}</strong></td>
                    <td>{client.companyCode}</td>
                    <td>{client.billingConfig?.pricingModel || '-'}</td>
                    <td>${client.billingConfig?.staffRate?.toFixed(2) || '0.00'}</td>
                    <td>${client.billingConfig?.patientRate?.toFixed(2) || '0.00'}</td>
                    <td>{client.billingConfig?.billingEmail || <em>Not set</em>}</td>
                    <td><span className={`badge ${client.status}`}>{client.status}</span></td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={() => onEdit(client)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function InvoicesPage({ invoices, clients }) {
  const [filter, setFilter] = useState('all');

  const filteredInvoices = filter === 'all'
    ? invoices
    : invoices.filter(i => i.status === filter);

  return (
    <>
      <div className="page-header">
        <h1>Invoices</h1>
        <p>View and manage all invoices</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>All Invoices</h3>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd' }}
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>

        {filteredInvoices.length === 0 ? (
          <div className="empty-state">
            <h3>No invoices found</h3>
            <p>Invoices will be automatically generated on the 1st of each month</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Period</th>
                  <th>Avg Staff</th>
                  <th>Avg Patients</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(invoice => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoiceNumber || invoice.id}</td>
                    <td>{invoice.clientName}</td>
                    <td>
                      {invoice.billingPeriod?.start} - {invoice.billingPeriod?.end}
                    </td>
                    <td>{invoice.averageStaffCount?.toFixed(1) || '-'}</td>
                    <td>{invoice.averagePatientCount?.toFixed(1) || '-'}</td>
                    <td><strong>${(invoice.total || 0).toFixed(2)}</strong></td>
                    <td><span className={`badge ${invoice.status}`}>{invoice.status}</span></td>
                    <td>{invoice.dueDate || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function UsagePage({ clients }) {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [usageData, setUsageData] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadUsage = async (clientId) => {
    if (!clientId) return;
    setLoading(true);
    try {
      const snapshotsRef = collection(db, 'usageSnapshots', clientId, 'days');
      const snap = await getDocs(query(snapshotsRef, orderBy('date', 'desc')));
      setUsageData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading usage:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedClientId) {
      loadUsage(selectedClientId);
    }
  }, [selectedClientId]);

  return (
    <>
      <div className="page-header">
        <h1>Usage Reports</h1>
        <p>Daily user count snapshots by client</p>
      </div>

      <div className="card">
        <div className="form-group">
          <label>Select Client</label>
          <select
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
          >
            <option value="">-- Select a client --</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : usageData.length === 0 ? (
          <div className="empty-state">
            <h3>No usage data</h3>
            <p>Usage snapshots are pulled daily at 11:59 PM UTC</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Staff</th>
                  <th>Patients</th>
                  <th>POAs</th>
                  <th>Total Users</th>
                </tr>
              </thead>
              <tbody>
                {usageData.slice(0, 30).map(day => (
                  <tr key={day.id}>
                    <td>{day.date}</td>
                    <td>{day.staffCount || 0}</td>
                    <td>{day.patientCount || 0}</td>
                    <td>{day.poaCount || 0}</td>
                    <td><strong>{(day.staffCount || 0) + (day.patientCount || 0) + (day.poaCount || 0)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function BillingConfigModal({ client, onClose, onSave }) {
  const [config, setConfig] = useState({
    pricingModel: client.billingConfig?.pricingModel || 'per_user',
    staffRate: client.billingConfig?.staffRate || 5,
    patientRate: client.billingConfig?.patientRate || 3,
    poaRate: client.billingConfig?.poaRate || 0,
    billingEmail: client.billingConfig?.billingEmail || '',
    billingContactName: client.billingConfig?.billingContactName || '',
    invoiceDueDays: client.billingConfig?.invoiceDueDays || 30,
    notes: client.billingConfig?.notes || '',
  });

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Billing - {client.name}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Pricing Model</label>
            <select
              value={config.pricingModel}
              onChange={(e) => handleChange('pricingModel', e.target.value)}
            >
              <option value="per_user">Per User</option>
              <option value="tiered_flat">Tiered Flat Rate</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {config.pricingModel === 'per_user' && (
            <div className="form-row">
              <div className="form-group">
                <label>Staff Rate ($/month)</label>
                <input
                  type="number"
                  step="0.01"
                  value={config.staffRate}
                  onChange={(e) => handleChange('staffRate', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="form-group">
                <label>Patient Rate ($/month)</label>
                <input
                  type="number"
                  step="0.01"
                  value={config.patientRate}
                  onChange={(e) => handleChange('patientRate', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="form-group">
                <label>POA Rate ($/month)</label>
                <input
                  type="number"
                  step="0.01"
                  value={config.poaRate}
                  onChange={(e) => handleChange('poaRate', parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Billing Contact Name</label>
              <input
                type="text"
                value={config.billingContactName}
                onChange={(e) => handleChange('billingContactName', e.target.value)}
                placeholder="John Smith"
              />
            </div>
            <div className="form-group">
              <label>Billing Email</label>
              <input
                type="email"
                value={config.billingEmail}
                onChange={(e) => handleChange('billingEmail', e.target.value)}
                placeholder="billing@company.com"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Invoice Due Days</label>
            <input
              type="number"
              value={config.invoiceDueDays}
              onChange={(e) => handleChange('invoiceDueDays', parseInt(e.target.value) || 30)}
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <input
              type="text"
              value={config.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Internal notes about this client"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(config)}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

export default App;
